// Stay hover video (<stay-hover-video>): a muted clip over its first frame, played once. Used by the step cards.
// - Hover and pointer (a mouse): plays from the start on mouseenter, holds its last frame, and fades back to the
//   first frame on mouseleave, over the target (the closest [data-stay-hover-video-target], else the element itself).
//   The video is decoration: no focus, no controls.
// - Touch screens: plays once, the first time the target is at least 60% on screen, then fades back.
// - Never with reduced motion (the CSS also hides the video). The poster is an image under the video (the markup's).
// - preload="none": the source is set only once the target is near the screen. Only opacity changes (.is-playing),
//   and only once the video is actually playing, so it never flashes in empty.
if (!customElements.get('stay-hover-video')) {
  customElements.define(
    'stay-hover-video',
    class StayHoverVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.target = this.closest('[data-stay-hover-video-target]') || this;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.start = this.start.bind(this);
        this.stop = this.stop.bind(this);

        this.video.addEventListener('playing', () => this.classList.add('is-playing'));
        this.video.addEventListener('ended', () => {
          if (!this.finePointer.matches) this.stop();
        });
        this.target.addEventListener('mouseenter', () => this.finePointer.matches && this.start());
        this.target.addEventListener('mouseleave', () => this.finePointer.matches && this.stop());
        this.reducedMotion.addEventListener('change', this.stop);

        this.nearObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.nearObserver.disconnect();
            this.load();
          },
          { rootMargin: '300px' }
        );
        this.nearObserver.observe(this.target);

        this.viewObserver = new IntersectionObserver(
          (entries) => {
            if (this.finePointer.matches || !entries.some((entry) => entry.isIntersecting)) return;
            this.viewObserver.disconnect();
            this.start();
          },
          { threshold: 0.6 }
        );
        this.viewObserver.observe(this.target);
      }

      disconnectedCallback() {
        this.nearObserver?.disconnect();
        this.viewObserver?.disconnect();
        this.reducedMotion?.removeEventListener('change', this.stop);
        clearTimeout(this.pauseTimer);
      }

      load() {
        if (this.reducedMotion.matches || this.video.getAttribute('src')) return;
        this.video.src = this.video.dataset.src;
        this.video.preload = 'auto';
        this.video.load();
      }

      start() {
        if (this.reducedMotion.matches) return;
        this.load();
        clearTimeout(this.pauseTimer);
        this.video.currentTime = 0;
        this.video.play().catch(() => {});
      }

      // Fade back to the first frame, then stop the video once it's hidden
      stop() {
        this.classList.remove('is-playing');
        clearTimeout(this.pauseTimer);
        this.pauseTimer = setTimeout(() => this.video.pause(), 500);
      }
    }
  );
}
